import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    Alert,
    Box,
    Chip,
    Collapse,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputAdornment,
    InputLabel,
    LinearProgress,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import {
    Close,
    KeyboardArrowDown,
    KeyboardArrowUp,
    Refresh,
    Search,
} from "@mui/icons-material";
import {
    CourierWebhookEvent,
    courierService,
} from "../../services/courierService";

const EVENT_TYPES = [
    "delivery_status",
    "tracking_update",
    "consignment_update",
    "cancel_request",
    "return_request",
    "return_list_accepted",
    "pickup_request",
    "payment_request",
    "user_update",
];

const RESULT_LOOK: Record<
    CourierWebhookEvent["result"],
    { label: string; color: "success" | "default" | "warning" | "error" | "info" }
> = {
    processed: { label: "Processed", color: "success" },
    no_change: { label: "No change", color: "default" },
    recorded: { label: "Recorded", color: "info" },
    duplicate: { label: "Retry (ignored)", color: "default" },
    order_not_found: { label: "Order not found", color: "warning" },
    invalid: { label: "Invalid", color: "warning" },
    bad_signature: { label: "Bad signature", color: "error" },
    error: { label: "Error", color: "error" },
};

const formatTime = (iso: string) =>
    new Date(iso).toLocaleString("en-GB", {
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
    });

function EventRow({ event }: { event: CourierWebhookEvent }) {
    const [open, setOpen] = useState(false);
    const look = RESULT_LOOK[event.result] ?? { label: event.result, color: "default" as const };
    return (
        <>
            <TableRow hover onClick={() => setOpen((v) => !v)} sx={{ cursor: "pointer", "& > td": { borderBottom: open ? 0 : undefined } }}>
                <TableCell padding="checkbox">
                    <IconButton size="small" aria-label={open ? "Hide details" : "Show details"}>
                        {open ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
                    </IconButton>
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>{formatTime(event.create_time)}</TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>{event.notification_type}</TableCell>
                <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                        {event.invoice || "—"}
                    </Typography>
                    {event.consignment_id && (
                        <Typography variant="caption" color="text.secondary">
                            #{event.consignment_id}
                        </Typography>
                    )}
                </TableCell>
                <TableCell>{event.status || "—"}</TableCell>
                <TableCell>
                    <Chip size="small" label={look.label} color={look.color} variant={look.color === "default" ? "outlined" : "filled"} />
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell colSpan={6} sx={{ py: 0 }}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ py: 1.5, display: "flex", flexDirection: "column", gap: 1 }}>
                            {event.result_detail && (
                                <Typography variant="body2">{event.result_detail}</Typography>
                            )}
                            <Typography variant="caption" color="text.secondary">
                                Answered HTTP {event.http_status} · Signature{" "}
                                {event.signature_valid === null || event.signature_valid === undefined
                                    ? "not sent"
                                    : event.signature_valid
                                      ? "valid"
                                      : "INVALID"}
                                {event.idempotency_key ? ` · Idempotency-Key ${event.idempotency_key}` : ""}
                            </Typography>
                            <Box
                                component="pre"
                                sx={{
                                    m: 0,
                                    p: 1.5,
                                    bgcolor: "grey.100",
                                    borderRadius: 1,
                                    fontSize: 12,
                                    overflowX: "auto",
                                    maxHeight: 260,
                                }}
                            >
                                {JSON.stringify(event.payload, null, 2)}
                            </Box>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
}

interface CourierWebhookLogDialogProps {
    open: boolean;
    onClose: () => void;
}

/**
 * Every webhook call Steadfast made to us, newest first, with what we did
 * with it — so "did Steadfast tell us?" can always be answered from our side.
 */
export const CourierWebhookLogDialog: React.FC<CourierWebhookLogDialogProps> = ({ open, onClose }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [events, setEvents] = useState<CourierWebhookEvent[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [eventType, setEventType] = useState("");
    const [searchDraft, setSearchDraft] = useState("");
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const requestRef = useRef(0);

    // Search after typing pauses.
    useEffect(() => {
        const t = setTimeout(() => {
            setSearch(searchDraft.trim());
            setPage(0);
        }, 300);
        return () => clearTimeout(t);
    }, [searchDraft]);

    const load = useCallback(async () => {
        const request = ++requestRef.current;
        setLoading(true);
        setError("");
        try {
            const result = await courierService.getWebhookEvents({
                notification_type: eventType || undefined,
                search: search || undefined,
                skip: page * rowsPerPage,
                limit: rowsPerPage,
            });
            if (request !== requestRef.current) return;
            setEvents(result.items);
            setTotal(result.total);
        } catch (err: any) {
            if (request !== requestRef.current) return;
            setError(err?.response?.data?.detail || "Couldn't load the webhook activity.");
        } finally {
            if (request === requestRef.current) setLoading(false);
        }
    }, [eventType, search, page, rowsPerPage]);

    useEffect(() => {
        if (open) void load();
    }, [open, load]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth fullScreen={isMobile}>
            <DialogTitle component="div" sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" component="h2">
                        Steadfast webhook activity
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Every call Steadfast made to us, newest first, and what we did with it.
                    </Typography>
                </Box>
                <Tooltip title="Refresh">
                    <span>
                        <IconButton onClick={() => void load()} disabled={loading} aria-label="Refresh">
                            <Refresh />
                        </IconButton>
                    </span>
                </Tooltip>
                <IconButton onClick={onClose} aria-label="Close">
                    <Close />
                </IconButton>
            </DialogTitle>
            <Box sx={{ height: 4 }}>{loading && <LinearProgress />}</Box>
            <DialogContent sx={{ pt: 1 }}>
                <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 2 }}>
                    <TextField
                        size="small"
                        placeholder="Invoice or consignment"
                        value={searchDraft}
                        onChange={(e) => setSearchDraft(e.target.value)}
                        sx={{ minWidth: 240, flex: { xs: 1, sm: "none" } }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <FormControl size="small" sx={{ minWidth: 200, flex: { xs: 1, sm: "none" } }}>
                        <InputLabel id="webhook-event-type-label" shrink>
                            Event
                        </InputLabel>
                        <Select
                            labelId="webhook-event-type-label"
                            label="Event"
                            notched
                            displayEmpty
                            value={eventType}
                            onChange={(e) => {
                                setEventType(e.target.value);
                                setPage(0);
                            }}
                        >
                            <MenuItem value="">All events</MenuItem>
                            {EVENT_TYPES.map((type) => (
                                <MenuItem key={type} value={type}>
                                    {type}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <TableContainer sx={{ border: 1, borderColor: "divider", borderRadius: 1 }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox" />
                                <TableCell>Received</TableCell>
                                <TableCell>Event</TableCell>
                                <TableCell>Invoice / consignment</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Result</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {events.map((event) => (
                                <EventRow key={event.event_id} event={event} />
                            ))}
                            {!loading && events.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6} sx={{ py: 4, textAlign: "center", color: "text.secondary" }}>
                                        {search || eventType ? "No calls match these filters." : "No webhook calls yet."}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={total}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                    rowsPerPageOptions={[25, 50, 100]}
                />
            </DialogContent>
        </Dialog>
    );
};
